import React, { useState, useEffect } from 'react';
import { 
  X, Loader2, CheckCircle, AlertCircle, 
  Send, Image as ImageIcon, Film, ExternalLink,
  Clock, Calendar, Zap, Sparkles, RefreshCw,
  MessageSquare, Target, Megaphone
} from 'lucide-react';
import { socialConnectionsService } from '../../services/SocialConnectionsService';
import { aiGenerationService } from '../../services/aiGenerationService';

// Ícones SVG das plataformas
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Ícones para estilos de legenda
const captionStyleIcons = {
  engajamento: MessageSquare,
  informativo: Target,
  'call-to-action': Megaphone
};

const captionStyleLabels = {
  engajamento: 'Engajamento',
  informativo: 'Informativo',
  'call-to-action': 'Call to Action'
};

const captionStyleColors = {
  engajamento: 'from-blue-500 to-cyan-500',
  informativo: 'from-green-500 to-emerald-500',
  'call-to-action': 'from-orange-500 to-red-500'
};

/**
 * Modal para publicar conteúdo no Instagram/TikTok
 * Com opção de publicar agora ou agendar para depois
 * E geração de legendas com IA
 */
const PublishToSocialModal = ({ 
  isOpen, 
  onClose, 
  content,
  batchId,
  onPublishSuccess 
}) => {
  const [connections, setConnections] = useState({});
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [caption, setCaption] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [publishResult, setPublishResult] = useState(null);

  // Estados para agendamento
  const [publishMode, setPublishMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // ✨ Estados para geração de legendas com IA
  const [isGeneratingCaptions, setIsGeneratingCaptions] = useState(false);
  const [generatedCaptions, setGeneratedCaptions] = useState([]);
  const [showCaptionSuggestions, setShowCaptionSuggestions] = useState(false);
  const [captionError, setCaptionError] = useState(null);

  // Carregar conexões ao abrir modal
  useEffect(() => {
    if (isOpen) {
      fetchConnections();
      // Reset state
      setSelectedPlatform(content?._isCarousel ? 'instagram' : null);
      setCaption('');
      setPublishStatus(null);
      setPublishError(null);
      setPublishResult(null);
      setPublishMode('now');
      setScheduledDate('');
      setScheduledTime('');
      // Reset estados de legenda
      setGeneratedCaptions([]);
      setShowCaptionSuggestions(false);
      setCaptionError(null);
    }
  }, [isOpen, content]);

  // Combinar data e hora em ISO string
  const getScheduledDateTime = () => {
    if (!scheduledDate || !scheduledTime) return null;
    const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    return dateTime.toISOString();
  };

  // Validar se data/hora é válida (no futuro)
  const isScheduleValid = () => {
    if (publishMode !== 'schedule') return true;
    if (!scheduledDate || !scheduledTime) return false;
    
    const scheduled = new Date(`${scheduledDate}T${scheduledTime}`);
    const now = new Date();
    now.setMinutes(now.getMinutes() + 4);
    
    return scheduled > now;
  };

  const fetchConnections = async () => {
    setIsLoadingConnections(true);
    try {
      const result = await socialConnectionsService.getConnections();
      setConnections(result.platforms || {});
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setIsLoadingConnections(false);
    }
  };

  // ✨ Gerar legendas com IA
  const handleGenerateCaptions = async () => {
    if (!content?.result_url) {
      setCaptionError('Imagem não disponível para análise');
      return;
    }

    setIsGeneratingCaptions(true);
    setCaptionError(null);
    setGeneratedCaptions([]);

    try {
      const contentType = content.content_type_category || 'marketing';
      
      let userContext = '';
      if (content.prompt) {
        userContext = `Contexto do conteúdo: ${content.prompt}`;
      }

      const captions = await aiGenerationService.generatePostCaptions({
        imageUrl: content.result_url,
        contentType,
        userContext
      });

      if (captions && captions.length > 0) {
        setGeneratedCaptions(captions);
        setShowCaptionSuggestions(true);
      } else {
        setCaptionError('Não foi possível gerar legendas. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao gerar legendas:', error);
      setCaptionError(
        error.response?.data?.detail || 
        error.message || 
        'Erro ao gerar legendas. Tente novamente.'
      );
    } finally {
      setIsGeneratingCaptions(false);
    }
  };

  // ✨ Selecionar uma legenda gerada
  const handleSelectCaption = (captionText) => {
    setCaption(captionText);
    setShowCaptionSuggestions(false);
  };

  const handlePublish = async () => {
    if (!selectedPlatform || !content) return;

    if (publishMode === 'schedule' && !isScheduleValid()) {
      setPublishError('Selecione uma data e hora válida (no futuro)');
      setPublishStatus('error');
      return;
    }

    setIsPublishing(true);
    setPublishStatus(null);
    setPublishError(null);

    try {
      let result;

      const effectiveBatchId = isCarousel ? carouselBatch?.id || batchId : batchId;

      if (publishMode === 'schedule') {
        result = await socialConnectionsService.schedulePost(
          selectedPlatform,
          content.id,
          effectiveBatchId,
          getScheduledDateTime(),
          caption.trim() || undefined,
          'America/Sao_Paulo',
          isCarousel
        );
      } else {
        result = await socialConnectionsService.publishSingle(
          selectedPlatform,
          content.id,
          effectiveBatchId,
          { caption: caption.trim() || undefined, isCarousel }
        );
      }

      setPublishStatus('success');
      setPublishResult(result);
      
      setTimeout(() => {
        if (onPublishSuccess) {
          onPublishSuccess(selectedPlatform, content.id, result, publishMode === 'schedule');
        }
      }, 1500);

    } catch (error) {
      console.error('Erro ao publicar:', error);
      setPublishStatus('error');
      setPublishError(error.response?.data?.detail || error.message || 'Erro ao publicar. Tente novamente.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = () => {
    if (!isPublishing) {
      onClose();
    }
  };

  const formatScheduledDate = () => {
    if (!scheduledDate || !scheduledTime) return '';
    const date = new Date(`${scheduledDate}T${scheduledTime}`);
    return date.toLocaleString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !content) return null;

  const isCarousel = content._isCarousel === true;
  const carouselBatch = content._batch;

  const instagramConnected = connections.instagram?.is_connected;
  const tiktokConnected = connections.tiktok?.is_connected;
  const hasAnyConnection = instagramConnected || (tiktokConnected && !isCarousel);

  const publishedPlatforms = content.published_to || [];
  const isPublishedOnInstagram = publishedPlatforms.includes('instagram');
  const isPublishedOnTikTok = publishedPlatforms.includes('tiktok');

  // Verificar se deve mostrar o footer
  const showFooter = !isLoadingConnections && hasAnyConnection && publishStatus !== 'success';

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full flex flex-col border-2 border-gray-200"
        style={{ maxHeight: 'calc(100vh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixo */}
        <div className="bg-black text-white px-5 py-3 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-amber-400" />
            <h2 className="text-base font-bold">Publicar nas Redes</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isPublishing}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Preview - Fixo */}
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex gap-3">
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
              {content.type === 'video' ? (
                <div className="w-full h-full bg-black flex items-center justify-center">
                  <Film className="w-5 h-5 text-white" />
                </div>
              ) : (
                <img 
                  src={content.result_url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex items-center gap-2">
                {isCarousel ? (
                  <>
                    <ImageIcon className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-sm font-medium text-purple-700">
                      Carrossel ({carouselBatch?.contents?.filter(c => c.status === 'completed').length || 0} slides)
                    </span>
                  </>
                ) : content.type === 'video' ? (
                  <>
                    <Film className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">Vídeo</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-900">Imagem</span>
                  </>
                )}
              </div>
              
              {publishedPlatforms.length > 0 && (
                <div className="flex gap-1.5 mt-1">
                  {isPublishedOnInstagram && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-pink-50 text-pink-700 rounded text-xs font-medium">
                      <InstagramIcon className="w-2.5 h-2.5" />
                      Publicado
                    </span>
                  )}
                  {isPublishedOnTikTok && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                      <TikTokIcon className="w-2.5 h-2.5" />
                      Publicado
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conteúdo Scrollável */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Loading Connections */}
          {isLoadingConnections ? (
            <div className="p-6 text-center">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Carregando conexões...</p>
            </div>
          ) : publishStatus === 'success' ? (
            /* Success State */
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">
                {publishMode === 'schedule' ? 'Agendado!' : 'Publicado!'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {publishMode === 'schedule' 
                  ? `Será publicado em ${formatScheduledDate()}.`
                  : `Publicado no ${selectedPlatform === 'instagram' ? 'Instagram' : 'TikTok'}.`
                }
              </p>
              <button
                onClick={handleClose}
                className="px-5 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-amber-500 hover:to-orange-600 transition-all"
              >
                Fechar
              </button>
            </div>
          ) : !hasAnyConnection ? (
            /* No Connections */
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Conecte suas Redes</h3>
              <p className="text-sm text-gray-600 mb-4">
                Conecte uma conta do Instagram ou TikTok primeiro.
              </p>
              <a
                href="/social"
                className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-amber-500 hover:to-orange-600 transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                Conectar
              </a>
            </div>
          ) : (
            /* Platform Selection, Schedule & Caption */
            <div className="p-4 space-y-3">
              {/* Error Message */}
              {publishStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="text-xs">{publishError}</span>
                </div>
              )}

              {/* Platform Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Plataforma
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {/* Instagram */}
                  <button
                    onClick={() => setSelectedPlatform('instagram')}
                    disabled={!instagramConnected || isPublishedOnInstagram || isPublishing}
                    className={`p-2.5 rounded-lg border-2 transition-all flex items-center gap-2 ${
                      selectedPlatform === 'instagram'
                        ? 'border-pink-500 bg-pink-50'
                        : instagramConnected && !isPublishedOnInstagram
                        ? 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/50'
                        : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedPlatform === 'instagram' ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' : 'bg-gradient-to-br from-purple-400 via-pink-400 to-orange-300'
                    }`}>
                      <InstagramIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Instagram</span>
                      {!instagramConnected ? (
                        <span className="text-xs text-gray-500">Não conectado</span>
                      ) : isPublishedOnInstagram ? (
                        <span className="text-xs text-green-600">✓ Publicado</span>
                      ) : (
                        <span className="text-xs text-gray-500 truncate block">@{connections.instagram?.username || 'conectado'}</span>
                      )}
                    </div>
                  </button>

                  {/* TikTok */}
                  <button
                    onClick={() => setSelectedPlatform('tiktok')}
                    disabled={!tiktokConnected || isPublishedOnTikTok || isPublishing || isCarousel}
                    className={`p-2.5 rounded-lg border-2 transition-all flex items-center gap-2 ${
                      selectedPlatform === 'tiktok'
                        ? 'border-gray-900 bg-gray-50'
                        : tiktokConnected && !isPublishedOnTikTok && !isCarousel
                        ? 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                        : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedPlatform === 'tiktok' ? 'bg-black' : 'bg-gray-800'
                    }`}>
                      <TikTokIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">TikTok</span>
                      {isCarousel ? (
                        <span className="text-xs text-gray-500">Carrossel apenas Instagram</span>
                      ) : !tiktokConnected ? (
                        <span className="text-xs text-gray-500">Não conectado</span>
                      ) : isPublishedOnTikTok ? (
                        <span className="text-xs text-green-600">✓ Publicado</span>
                      ) : (
                        <span className="text-xs text-gray-500 truncate block">@{connections.tiktok?.username || 'conectado'}</span>
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {/* Modo de Publicação */}
              {selectedPlatform && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Quando?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPublishMode('now')}
                      disabled={isPublishing}
                      className={`p-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        publishMode === 'now'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                        publishMode === 'now' 
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                          : 'bg-gray-100'
                      }`}>
                        <Zap className={`w-3.5 h-3.5 ${publishMode === 'now' ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span className={`text-sm font-medium ${publishMode === 'now' ? 'text-amber-700' : 'text-gray-700'}`}>
                        Agora
                      </span>
                    </button>

                    <button
                      onClick={() => setPublishMode('schedule')}
                      disabled={isPublishing}
                      className={`p-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        publishMode === 'schedule'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                        publishMode === 'schedule' 
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                          : 'bg-gray-100'
                      }`}>
                        <Calendar className={`w-3.5 h-3.5 ${publishMode === 'schedule' ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <span className={`text-sm font-medium ${publishMode === 'schedule' ? 'text-amber-700' : 'text-gray-700'}`}>
                        Agendar
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Seletor de Data e Hora */}
              {selectedPlatform && publishMode === 'schedule' && (
                <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      disabled={isPublishing}
                      className="w-full px-2.5 py-1.5 text-sm border border-amber-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                    />
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      disabled={isPublishing}
                      className="w-full px-2.5 py-1.5 text-sm border border-amber-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
                    />
                  </div>
                  {scheduledDate && scheduledTime && !isScheduleValid() && (
                    <p className="mt-1.5 text-xs text-red-600">
                      ⚠️ Selecione data/hora no futuro
                    </p>
                  )}
                </div>
              )}

              {/* ✨ Caption Input com Geração de IA */}
              {selectedPlatform && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Legenda
                    </label>
                    <button
                      onClick={handleGenerateCaptions}
                      disabled={isGeneratingCaptions || isPublishing}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGeneratingCaptions ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          Gerar com IA
                        </>
                      )}
                    </button>
                  </div>

                  {/* Erro de geração */}
                  {captionError && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-100 rounded-md">
                      <p className="text-xs text-red-600">{captionError}</p>
                    </div>
                  )}

                  {/* ✨ Sugestões de Legendas Geradas */}
                  {showCaptionSuggestions && generatedCaptions.length > 0 && (
                    <div className="mb-2 space-y-1.5 animate-fadeIn">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          ✨ Sugestões da IA
                        </p>
                        <button
                          onClick={handleGenerateCaptions}
                          disabled={isGeneratingCaptions}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Gerar novas"
                        >
                          <RefreshCw className={`w-3 h-3 ${isGeneratingCaptions ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                      
                      {generatedCaptions.map((captionItem, index) => {
                        const StyleIcon = captionStyleIcons[captionItem.style] || MessageSquare;
                        const styleLabel = captionStyleLabels[captionItem.style] || captionItem.style;
                        const styleColor = captionStyleColors[captionItem.style] || 'from-gray-500 to-gray-600';
                        
                        return (
                          <button
                            key={index}
                            onClick={() => handleSelectCaption(captionItem.text)}
                            className="w-full text-left p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-purple-300 rounded-lg transition-all group"
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-5 h-5 rounded bg-gradient-to-br ${styleColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                <StyleIcon className="w-3 h-3 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span className="text-xs font-medium text-gray-700">
                                    {styleLabel}
                                  </span>
                                  <span className="text-xs text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                    • Usar
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-2">
                                  {captionItem.text}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Campo de texto da legenda */}
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Adicione uma legenda..."
                    rows={4}
                    maxLength={2200}
                    disabled={isPublishing}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-0.5 text-right">
                    {caption.length}/2200
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions - Sempre Fixo no Final */}
        {showFooter && (
          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-2xl">
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                disabled={isPublishing}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handlePublish}
                disabled={
                  !selectedPlatform || 
                  isPublishing || 
                  (publishMode === 'schedule' && !isScheduleValid())
                }
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-amber-500 hover:to-orange-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {publishMode === 'schedule' ? 'Agendando...' : 'Publicando...'}
                  </>
                ) : publishMode === 'schedule' ? (
                  <>
                    <Calendar className="w-4 h-4" />
                    Agendar
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Publicar
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishToSocialModal;